import { saveAs } from 'file-saver';
import type { Dispatch, AnyAction } from 'redux';

import * as DeleteAttachmentAPI from 'ab-api/web/attachment/deleteAttachment';

import type { PendingAttachmentVM } from 'ab-viewModels/attachment/pendingAttachment.viewModel';

import type DeletePendingAttachmentsRM from 'ab-requestModels/attachment/deletePendingAttachments.requestModel';
import type { AttachmentsForm, ResizeImageViewModel } from 'ab-requestModels/attachment.requestModel';

import type { BlobStorageContainer } from '@acceligentllc/shared/enums/blobStorageContainer';
import type { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';

import type { GetRootState } from 'af-reducers';

import { errorHandler } from 'af-utils/actions.util';
import { http } from 'af-utils/http.util';

import API from 'af-constants/routes/api';

import * as httpActions from 'af-reducers/http.reducer';

export function downloadAttachment(attachmentId: number, fileName: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = await http.get(API.V1.ATTACHMENT.DOWNLOAD(attachmentId), { responseType: 'blob' });
			saveAs(data, fileName);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function downloadAttachmentsAsZip(attachmentIds: number[], filename: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = await http.get(API.V1.ATTACHMENT.DOWNLOAD_AS_ZIP(attachmentIds), { responseType: 'blob' });
			saveAs(data, filename);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function downloadAttachmentForPreview(attachmentId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<{ data: ArrayBuffer; }>(API.V1.ATTACHMENT.DOWNLOAD_FOR_VIEWER(attachmentId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function getPresignedGetUrl(directory: string, filename: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<string>(API.V1.ATTACHMENT.SIGNED_URL(undefined, directory, filename));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function resizeImage(directory: string, filename: string, size: BlobStorageImageSizeContainer, addIfMissing: boolean = false) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post(API.V1.ATTACHMENT.RESIZE_IMAGE(), { directory, filename, size, addIfMissing } as ResizeImageViewModel);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

interface AttachmentFile {
	requestFiles?: File[];
}

export function uploadAttachmentsToDirectory(directoryId: number, file: AttachmentFile) {
	return async (dispatch: Dispatch<httpActions.UploadProgress | httpActions.UploadProgressSize>, { redirectTo }) => {
		const action = async () => {
			const options = {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
				onUploadProgress: (progressEvent) => {
					const totalLength = progressEvent.lengthComputable ? progressEvent.total : progressEvent.target.getResponseHeader('content-length') || progressEvent.target.getResponseHeader('x-decompressed-content-length');
					if (totalLength !== null) {
						dispatch(httpActions.UPLOAD_PROGRESS(Math.round((progressEvent.loaded * 100) / totalLength)));
						dispatch(httpActions.UPLOAD_PROGRESS_SIZE(progressEvent.loaded));
					}
				},
			};
			const fd = new FormData();
			file.requestFiles?.forEach((_file) => fd.append('requestFiles[]', _file));

			await http.post(API.V1.ATTACHMENT.UPLOAD_BULK(directoryId), fd, options);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};

}

export interface CopyAttachmentToWO {
	directoryId: number;
	copyToWO: boolean;
}
export function copyAttachmentToWO(attachmentId: number, directoryId: number, workRequestId: number, copyToWO: boolean) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			await http.put(API.V1.ATTACHMENT.COPY_TO_WO(attachmentId), { workRequestId, copyToWO, directoryId });
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function deleteAttachment(attachmentId: number, directoryId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const body: DeleteAttachmentAPI.W_Attachment_DeleteAttachment_RM = { directoryId };
			return await http.delete<void>(DeleteAttachmentAPI.URL(attachmentId), body);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function renameAttachment(directoryId: number, attachmentId: number, name: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.post(API.V1.ATTACHMENT.RENAME(attachmentId), { directoryId, name });
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function uploadPendingAttachments(form: AttachmentsForm, storageContainer: BlobStorageContainer) {
	return async (dispatch: Dispatch<httpActions.UploadProgress | httpActions.UploadProgressSize>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const options = {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			};
			const fd = new FormData();
			form.requestFiles?.forEach((_file) => fd.append('requestFiles[]', _file));
			return await http.post<PendingAttachmentVM[]>((API.V1.ATTACHMENT.ADD_PENDING(storageContainer)), fd, options);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function deletePendingAttachments(event: DeletePendingAttachmentsRM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.delete<void>(API.V1.ATTACHMENT.DELETE_PENDING, event);
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function changeAttachmentParent(attachmentId: number, sourceDirId: number, destinationDirId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.post(API.V1.ATTACHMENT.CHANGE_PARENT(attachmentId), { sourceDirId, destinationDirId });
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}
